import React, { useMemo, useState, useEffect, useId } from 'react'
import { Link } from 'react-router-dom'
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  Cell,
} from 'recharts'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { Alert } from '../../common/Alert'
import { Button } from '../../common/Button'
import { useRevenueSeasonality } from '../../../hooks/useRevenueSeasonality'
import {
  sortSeasonalityRows,
  buildFyLabelMap,
  allFinancialYearsSorted,
  filterByYears,
  buildSeasonalPattern,
  seasonalVariancePercent,
  peakAndChallengingMonths,
  buildYearComparisonRows,
  buildMultiYearMonthlyComparisonChart,
  num,
} from '../../../utils/revenueSeasonalityUtils'
import { YearMultiSelect } from './YearMultiSelect'
import { appPath } from '../../../utils/appPath'

function formatUsd(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return `$${Math.round(n).toLocaleString()}`
}

function yAxisTick(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  return `$${(n / 1_000).toFixed(0)}K`
}

const COMPARISON_LINE_COLORS = [
  '#2563eb',
  '#dc2626',
  '#059669',
  '#d97706',
  '#7c3aed',
  '#db2777',
]

const Skeleton: React.FC<{ className?: string }> = ({
  className = 'h-32',
}) => <div className={`rounded-xl bg-slate-200/80 animate-pulse ${className}`} />

const PAGE_TITLE = 'Annual revenue seasonality'

const linkToRevenueTrends = (
  <Link
    to={appPath('/revenue-trends')}
    className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline"
  >
    ← Revenue trends (MoM / rolling)
  </Link>
)

export const RevenueSeasonalityPage: React.FC = () => {
  const gradId = `seasonal-${useId().replace(/:/g, '')}`

  const { records, loading, error, refetch } = useRevenueSeasonality()
  const [selectedYears, setSelectedYears] = useState<number[]>([])
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false)
  const [yearCompareMode, setYearCompareMode] = useState<'chart' | 'table'>(
    'chart'
  )

  const sortedAll = useMemo(
    () => sortSeasonalityRows(records),
    [records]
  )
  const fyLabelMap = useMemo(
    () => buildFyLabelMap(sortedAll),
    [sortedAll]
  )
  const allFY = useMemo(
    () => allFinancialYearsSorted(sortedAll),
    [sortedAll]
  )

  useEffect(() => {
    if (!records.length) return
    setSelectedYears(prev => {
      if (prev.length > 0) return prev
      const latest = allFY[allFY.length - 1]
      return latest != null ? [latest] : []
    })
  }, [records, allFY])

  const hasSelection = selectedYears.length > 0
  const filtered = useMemo(
    () => (hasSelection ? filterByYears(sortedAll, selectedYears) : []),
    [sortedAll, selectedYears, hasSelection]
  )

  const totalRevenue = useMemo(
    () => filtered.reduce((s, r) => s + num(r.monthly_revenue), 0),
    [filtered]
  )
  const avgMonthlyRevenue =
    filtered.length > 0 ? totalRevenue / filtered.length : 0

  const seasonalPattern = useMemo(
    () =>
      hasSelection && filtered.length
        ? buildSeasonalPattern(filtered, selectedYears)
        : [],
    [filtered, selectedYears, hasSelection]
  )

  const variancePct = useMemo(
    () => seasonalVariancePercent(seasonalPattern, avgMonthlyRevenue),
    [seasonalPattern, avgMonthlyRevenue]
  )

  const { top3, bottom3 } = useMemo(
    () => peakAndChallengingMonths(seasonalPattern),
    [seasonalPattern]
  )

  const yearComparisonRows = useMemo(
    () =>
      hasSelection
        ? buildYearComparisonRows(
            filtered,
            sortedAll,
            selectedYears,
            fyLabelMap
          )
        : [],
    [filtered, sortedAll, selectedYears, fyLabelMap, hasSelection]
  )

  const { rows: comparisonChartRows, sortedYears: chartYears } = useMemo(
    () =>
      hasSelection
        ? buildMultiYearMonthlyComparisonChart(filtered, selectedYears)
        : { rows: [] as Record<string, unknown>[], sortedYears: [] as number[] },
    [filtered, selectedYears, hasSelection]
  )

  if (loading) {
    return (
      <AppLayout>
        <PageHeader title={PAGE_TITLE} underTitle={linkToRevenueTrends} />
        <div className="p-6 space-y-4 max-w-[1600px]">
          <Skeleton className="h-10 w-64" />
          <div className="grid md:grid-cols-3 gap-3">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
          <Skeleton className="h-72" />
        </div>
      </AppLayout>
    )
  }

  if (error) {
    return (
      <AppLayout>
        <PageHeader title={PAGE_TITLE} underTitle={linkToRevenueTrends} />
        <div className="p-6 max-w-lg">
          <Alert type="error" title="Could not load data" message={error} />
          <Button className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </AppLayout>
    )
  }

  if (records.length === 0) {
    return (
      <AppLayout>
        <PageHeader title={PAGE_TITLE} underTitle={linkToRevenueTrends} />
        <div className="p-6 max-w-lg">
          <Alert
            type="info"
            title="No data"
            message="revenue_seasonality.json is empty or missing."
          />
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <PageHeader
        title={PAGE_TITLE}
        underTitle={linkToRevenueTrends}
        filters={
          <div className="flex flex-wrap items-center gap-2 justify-end w-full min-w-0">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Years
            </span>
            <YearMultiSelect
              allFinancialYears={allFY}
              fyLabelMap={fyLabelMap}
              selectedYears={selectedYears}
              onSelectionChange={setSelectedYears}
              open={yearDropdownOpen}
              onOpenChange={setYearDropdownOpen}
            />
          </div>
        }
      />

      <div className="p-6 space-y-6 max-w-[1600px] pb-12">
        {!hasSelection ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-md p-10 text-center">
            <p className="text-slate-600 mb-4">
              Select at least one financial year to view seasonality.
            </p>
            <YearMultiSelect
              allFinancialYears={allFY}
              fyLabelMap={fyLabelMap}
              selectedYears={selectedYears}
              onSelectionChange={setSelectedYears}
              open={yearDropdownOpen}
              onOpenChange={setYearDropdownOpen}
            />
          </div>
        ) : filtered.length === 0 ? (
          <Alert
            type="info"
            title="No rows for selection"
            message="Try different years."
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4 border-l-4 border-l-blue-500">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                  Total revenue
                </p>
                <p className="text-xl font-bold tabular-nums text-slate-900">
                  {formatUsd(totalRevenue)}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4 border-l-4 border-l-emerald-500">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                  Monthly average
                </p>
                <p className="text-xl font-bold tabular-nums text-slate-900">
                  {formatUsd(avgMonthlyRevenue)}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4 border-l-4 border-l-amber-500">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                  Seasonal variance
                </p>
                <p className="text-xl font-bold tabular-nums text-amber-900">
                  {variancePct.toFixed(1)}%
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <h2 className="text-sm font-semibold text-slate-800">
                  Year-by-year revenue comparison
                </h2>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setYearCompareMode('chart')}
                    className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                      yearCompareMode === 'chart'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Chart
                  </button>
                  <button
                    type="button"
                    onClick={() => setYearCompareMode('table')}
                    className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                      yearCompareMode === 'table'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Table
                  </button>
                </div>
              </div>

              {yearCompareMode === 'chart' ? (
                <div
                  className={`w-full min-w-0 ${chartYears.length > 1 ? 'h-[280px]' : 'h-[220px]'}`}
                >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={comparisonChartRows}
                        margin={{
                          left: 8,
                          right: 8,
                          top: 8,
                          bottom: chartYears.length > 1 ? 36 : 8,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="monthShort"
                          stroke="#64748b"
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          stroke="#64748b"
                          width={56}
                          tick={{ fontSize: 10 }}
                          tickFormatter={yAxisTick}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                          formatter={(value: number, name: string) => [
                            value == null || Number.isNaN(Number(value))
                              ? '—'
                              : formatUsd(Number(value)),
                            name,
                          ]}
                          labelFormatter={(_, payload) => {
                            const p = payload?.[0]?.payload as {
                              monthName?: string
                            }
                            return p?.monthName || ''
                          }}
                        />
                        {chartYears.length === 1 ? (
                          <>
                            <defs>
                              <linearGradient
                                id={gradId}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#3b82f6"
                                  stopOpacity={0.35}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#3b82f6"
                                  stopOpacity={0.05}
                                />
                              </linearGradient>
                            </defs>
                            <Area
                              type="monotone"
                              dataKey={`rev_${chartYears[0]}`}
                              stroke="#2563eb"
                              strokeWidth={2}
                              fill={`url(#${gradId})`}
                            />
                            <Line
                              type="monotone"
                              dataKey={`rev_${chartYears[0]}`}
                              name={
                                fyLabelMap[chartYears[0]] ||
                                `FY ${chartYears[0]}`
                              }
                              stroke="#dc2626"
                              strokeWidth={2}
                              dot={{ r: 3, fill: '#dc2626' }}
                            />
                          </>
                        ) : (
                          <>
                            <Legend
                              wrapperStyle={{ fontSize: 11 }}
                              iconType="line"
                            />
                            {chartYears.map((y, i) => (
                              <Line
                                key={y}
                                type="monotone"
                                dataKey={`rev_${y}`}
                                name={
                                  fyLabelMap[y] ||
                                  `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`
                                }
                                stroke={
                                  COMPARISON_LINE_COLORS[
                                    i % COMPARISON_LINE_COLORS.length
                                  ]
                                }
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                connectNulls
                              />
                            ))}
                          </>
                        )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left py-2.5 px-3 font-semibold text-slate-600">
                          FY
                        </th>
                        <th className="text-right py-2.5 px-3 font-semibold text-slate-600">
                          Total
                        </th>
                        <th className="text-right py-2.5 px-3 font-semibold text-slate-600">
                          Avg / mo
                        </th>
                        <th className="text-right py-2.5 px-3 font-semibold text-slate-600">
                          Months
                        </th>
                        <th className="text-right py-2.5 px-3 font-semibold text-slate-600">
                          YoY
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearComparisonRows.map(row => (
                        <tr
                          key={row.year}
                          className="border-b border-slate-100 hover:bg-slate-50/80"
                        >
                          <td className="py-2.5 px-3 font-medium text-slate-900">
                            {row.fyLabel}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums">
                            {formatUsd(row.totalRevenue)}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-slate-600">
                            {formatUsd(row.avgPerMonth)}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-slate-600">
                            {row.monthCount}
                          </td>
                          <td
                            className={`py-2.5 px-3 text-right tabular-nums font-medium ${
                              row.yoyPct == null
                                ? 'text-slate-400'
                                : row.yoyPct > 0
                                  ? 'text-emerald-700'
                                  : row.yoyPct < 0
                                    ? 'text-rose-600'
                                    : 'text-slate-600'
                            }`}
                          >
                            {row.yoyPct == null
                              ? '—'
                              : `${row.yoyPct >= 0 ? '+' : ''}${row.yoyPct.toFixed(1)}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4">
              <h2 className="text-sm font-semibold text-slate-800 mb-3">
                Seasonal pattern (monthly averages)
              </h2>
              <div className="h-[280px] w-full min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={seasonalPattern}
                    margin={{ left: 8, right: 8, top: 8, bottom: 48 }}
                    barCategoryGap="12%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="monthShort"
                      stroke="#64748b"
                      tick={{ fontSize: 10 }}
                      angle={-35}
                      textAnchor="end"
                      height={56}
                    />
                    <YAxis
                      stroke="#64748b"
                      width={52}
                      tick={{ fontSize: 10 }}
                      tickFormatter={yAxisTick}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        fontSize: 12,
                        maxWidth: 320,
                      }}
                      formatter={(value: number) => [
                        formatUsd(value),
                        'Avg revenue',
                      ]}
                    />
                    <Bar dataKey="avgRevenue" radius={[4, 4, 0, 0]} maxBarSize={40}>
                      {seasonalPattern.map((entry, index) => {
                        const avg = entry.avgRevenue
                        const fill =
                          avg > avgMonthlyRevenue * 1.1
                            ? '#10b981'
                            : avg < avgMonthlyRevenue * 0.9
                              ? '#ef4444'
                              : '#f59e0b'
                        return <Cell key={`c-${index}`} fill={fill} />
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4">
                <h2 className="text-sm font-semibold text-slate-800 mb-3">
                  Peak performance months
                </h2>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="bg-emerald-50 border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">
                          #
                        </th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">
                          Month
                        </th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">
                          Avg revenue
                        </th>
                        {selectedYears.length > 1 && (
                          <th className="text-right py-2 px-3 font-semibold text-slate-600">
                            Best FY
                          </th>
                        )}
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">
                          Points
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {top3.map((m, i) => (
                        <tr
                          key={m.month}
                          className="border-b border-slate-100"
                        >
                          <td className="py-2 px-3 font-bold text-emerald-700">
                            {i + 1}
                          </td>
                          <td className="py-2 px-3 font-medium text-slate-900">
                            {m.monthName}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums">
                            {formatUsd(m.avgRevenue)}
                          </td>
                          {selectedYears.length > 1 && (
                            <td className="py-2 px-3 text-right text-[12px] text-slate-600">
                              {m.bestYear != null
                                ? fyLabelMap[m.bestYear] ||
                                  `FY ${m.bestYear}`
                                : '—'}
                            </td>
                          )}
                          <td className="py-2 px-3 text-right text-slate-600">
                            {m.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4">
                <h2 className="text-sm font-semibold text-slate-800 mb-3">
                  Challenging months
                </h2>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="bg-rose-50 border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">
                          #
                        </th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">
                          Month
                        </th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">
                          Avg revenue
                        </th>
                        {selectedYears.length > 1 && (
                          <th className="text-right py-2 px-3 font-semibold text-slate-600">
                            Weakest FY
                          </th>
                        )}
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">
                          Points
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bottom3.map((m, i) => (
                        <tr
                          key={m.month}
                          className="border-b border-slate-100"
                        >
                          <td className="py-2 px-3 font-bold text-rose-700">
                            {bottom3.length - i}
                          </td>
                          <td className="py-2 px-3 font-medium text-slate-900">
                            {m.monthName}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums">
                            {formatUsd(m.avgRevenue)}
                          </td>
                          {selectedYears.length > 1 && (
                            <td className="py-2 px-3 text-right text-[12px] text-slate-600">
                              {m.worstYear != null
                                ? fyLabelMap[m.worstYear] ||
                                  `FY ${m.worstYear}`
                                : '—'}
                            </td>
                          )}
                          <td className="py-2 px-3 text-right text-slate-600">
                            {m.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default RevenueSeasonalityPage
